import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BarChart3, Download, Loader2 } from 'lucide-react';
import { formatCurrency, formatDateShort } from '@/lib/utils';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Reports() {
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [supplierData, setSupplierData] = useState([]);
  const [budgetData, setBudgetData] = useState([]);
  const [agingData, setAgingData] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        const params = {};
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        const [expRes, supRes, budRes, agRes] = await Promise.all([
          axios.get(`${API_URL}/reports/expenses`, { params }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/reports/suppliers`, { params }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/reports/budget`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/reports/aging`).catch(() => ({ data: [] }))
        ]);
        setExpenses(expRes.data); setSupplierData(supRes.data); setBudgetData(budRes.data); setAgingData(agRes.data);
      } catch (e) {} finally { setLoading(false); }
    };
    fetch();
  }, [dateFrom, dateTo]);

  const handleExport = async (format) => {
    try {
      const res = await axios.get(`${API_URL}/reports/export/${format}`, { params: { date_from: dateFrom, date_to: dateTo }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `rapport_factures.${format === 'csv' ? 'csv' : 'xlsx'}`; a.click();
      toast.success('Export telecharge');
    } catch (e) { toast.error('Erreur export'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold"><BarChart3 className="w-6 h-6 inline mr-2" />Rapports</h1></div>
        <div className="flex gap-2">
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-40" />
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-40" />
          <Button variant="outline" onClick={() => handleExport('csv')}><Download className="w-4 h-4 mr-2" /> CSV</Button>
          <Button variant="outline" onClick={() => handleExport('excel')}><Download className="w-4 h-4 mr-2" /> Excel</Button>
        </div>
      </div>

      <Tabs defaultValue="expenses">
        <TabsList><TabsTrigger value="expenses">Dépenses mensuelles</TabsTrigger><TabsTrigger value="suppliers">Analyse fournisseurs</TabsTrigger><TabsTrigger value="budget">Suivi budget</TabsTrigger><TabsTrigger value="aging">Echeancier</TabsTrigger></TabsList>

        <TabsContent value="expenses">
          <Card><CardHeader><CardTitle>Évolution des dépenses</CardTitle></CardHeader><CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={expenses}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="period" /><YAxis /><Tooltip formatter={v => formatCurrency(v)} />
                <Area type="monotone" dataKey="montant_ht" stroke="#0F5156" fill="#0F515620" name="Montant HT" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="suppliers">
          <Card><CardHeader><CardTitle>Top fournisseurs</CardTitle></CardHeader><CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={supplierData.slice(0, 10)} layout="vertical"><CartesianGrid strokeDasharray="3 3" /><XAxis type="number" /><YAxis dataKey="supplier_name" type="category" width={150} /><Tooltip formatter={v => formatCurrency(v)} />
                <Bar dataKey="total_ht" fill="#0F5156" name="Total HT" />
              </BarChart>
            </ResponsiveContainer>
            <Table className="mt-4"><TableHeader><TableRow><TableHead>Fournisseur</TableHead><TableHead>Nb Factures</TableHead><TableHead>Total HT</TableHead><TableHead>Total TTC</TableHead></TableRow></TableHeader>
            <TableBody>{supplierData.map((s, i) => (
              <TableRow key={i}><TableCell className="font-medium">{s.supplier_name}</TableCell><TableCell>{s.invoice_count}</TableCell><TableCell>{formatCurrency(s.total_ht)}</TableCell><TableCell>{formatCurrency(s.total_ttc)}</TableCell></TableRow>
            ))}</TableBody></Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="budget">
          <Card><CardHeader><CardTitle>Suivi budgétaire</CardTitle></CardHeader><CardContent>
            <Table><TableHeader><TableRow><TableHead>Catégorie</TableHead><TableHead>Budget prevu</TableHead><TableHead>Engage</TableHead><TableHead>Paye</TableHead><TableHead>Ecart</TableHead></TableRow></TableHeader>
            <TableBody>{budgetData.map((b, i) => (
              <TableRow key={i}><TableCell className="font-medium">{b.categorie || b.departement || '-'}</TableCell><TableCell>{formatCurrency(b.budget_prevu)}</TableCell><TableCell>{formatCurrency(b.budget_engage)}</TableCell><TableCell>{formatCurrency(b.budget_paye)}</TableCell>
                <TableCell className={b.budget_prevu - b.budget_engage < 0 ? 'text-red-600' : 'text-green-600'}>{formatCurrency(b.budget_prevu - b.budget_engage)}</TableCell>
              </TableRow>
            ))}{budgetData.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-500">Aucun budget configure</TableCell></TableRow>}</TableBody></Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="aging">
          <Card><CardHeader><CardTitle>Echeancier des factures</CardTitle></CardHeader><CardContent>
            <div className="grid grid-cols-4 gap-4 mb-6">
              {[{ label: '0-30 jours', key: '0_30' }, { label: '30-60 jours', key: '30_60' }, { label: '60-90 jours', key: '60_90' }, { label: '> 90 jours', key: '90_plus' }].map(bracket => {
                const data = agingData.find(d => d.bracket === bracket.key) || { count: 0, total: 0 };
                return (
                  <Card key={bracket.key}><CardContent className="pt-4 text-center">
                    <p className="text-sm text-slate-500">{bracket.label}</p>
                    <p className="text-xl font-bold text-[#0F5156]">{formatCurrency(data.total)}</p>
                    <p className="text-sm text-slate-400">{data.count} facture(s)</p>
                  </CardContent></Card>
                );
              })}
            </div>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
